import { useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, XCircle } from "lucide-react";
import { useGcpStart } from "@/hooks/queries/use-cloud";
import { usePageTitle } from "@/hooks/use-page-title";
import { Button } from "@/components/ui/button";
import { gcpWizardPath, writeGcpWizardState } from "@/components/cloud/gcp-wizard/wizard-state";

/** Where Google sends the admin back after signing in. Keeps the sealed sign-in and moves on to step 3. */
export function GoogleCloudCallbackPage() {
  usePageTitle("Connecting Google Cloud");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const start = useGcpStart();
  const started = useRef(false);

  const code = params.get("code");
  const state = params.get("state");

  useEffect(() => {
    if (started.current || !code || !state) return;
    started.current = true;
    start.mutate(
      { code, state },
      {
        onSuccess: (result) => {
          writeGcpWizardState({ session: result.session, ...(result.projectId ? { projectMode: "existing", projectId: result.projectId } : {}) });
          void navigate(gcpWizardPath("project"), { replace: true });
        },
      }
    );
  }, [code, state, start, navigate]);

  if (start.isError || !code || !state) {
    const message =
      start.error instanceof Error
        ? start.error.message
        : params.get("error") === "access_denied"
          ? "Google Cloud wasn't connected: the permission wasn't approved."
          : "Google didn't send the sign-in back. Try Connect Google Cloud again.";
    return (
      <div className="mx-auto max-w-md space-y-4 pt-16 text-center">
        <XCircle className="mx-auto h-8 w-8 text-destructive" />
        <p className="text-sm">{message}</p>
        <Button asChild size="sm" variant="outline">
          <Link to={gcpWizardPath("start")}>Back to the wizard</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-sm space-y-4 px-4 pt-16 text-center text-sm">
      <Loader2 className="mx-auto h-6 w-6 animate-spin" />
      <p>Finishing your Google sign-in…</p>
    </div>
  );
}
